import { RefObject, useCallback, useEffect, useState } from 'react';

interface RsuitePickerHandle {
  overlay?: HTMLElement | null;
}

/**
 * Makes Enter apply the pending selection of an open rsuite DateRangePicker
 * by clicking the popup's OK button (which commits the range and closes it).
 * Wire the returned handlers to the picker's onOpen/onClose props.
 */
export const useEnterAppliesDateRange = (
  pickerRef: RefObject<RsuitePickerHandle | null>,
) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Plain Enter only — Cmd/Ctrl+Enter is the global Apply shortcut
      if (
        event.key !== 'Enter' ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const overlay = pickerRef.current?.overlay;
      if (!overlay) return;

      const okButton = overlay.querySelector<HTMLButtonElement>(
        '.rs-picker-toolbar-right .rs-btn-primary',
      );
      // OK is disabled while the range is incomplete — do nothing then
      if (!okButton || okButton.disabled) return;

      event.preventDefault();
      event.stopPropagation();
      okButton.click();
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, pickerRef]);

  return { handleOpen, handleClose };
};
